"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import styles from "./medical-directory.module.css"
import { SearchBar, SearchBarHandle, SelectedLocation } from "./search-bar"
import { ResultsAndMapWrapper } from "./results-and-map-wrapper"
import MentalHealth from "./results/mental-health"
import { Professional, SearchResults } from "@/hooks/useSearch"
import { trackProviderDetailViewed } from "@/lib/mixpanelClient"
import {
  sortByDistanceHybrid,
  localidadCenter,
  provinciaCenter,
  centroidOf,
  parseCoords,
  normalizeLocalityName,
  LatLng,
} from "@/lib/geo"

export function MedicalDirectory() {
  const [searchResults, setSearchResults] = useState<SearchResults | undefined>(undefined)
  const [selectedProfessional, setSelectedProfessional] = useState<Professional | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>("")
  const [selectedPlan, setSelectedPlan] = useState<string>("")
  const [cartillaData, setCartillaData] = useState<{ nombrePdf: string; url: string } | null>(null)
  // La búsqueda por la zona elegida no encontró nada y se amplió sola a
  // nivel país (ver SearchBar.handleSearch): mostramos un aviso.
  const [nationwideFallbackActive, setNationwideFallbackActive] = useState(false)
  // true en el fallback automático de arriba Y en el "Todos" manual — para
  // no forzar el radio a 5km sobre una búsqueda que ya es a propósito país
  // completo (ver ResultsAndMapWrapper).
  const [isNationwideSearch, setIsNationwideSearch] = useState(false)

  // Localidad/zona elegida (acota la consulta) y posición GPS exacta cuando el
  // usuario usa "Mi ubicación" (centro de cercanía preciso).
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(null)
  const [gpsCenter, setGpsCenter] = useState<LatLng | null>(null)

  // Ref al SearchBar para poder disparar la búsqueda "Todos" (país completo,
  // sin Pcia/Zona) desde el botón que vive en ResultsAndMapWrapper.
  const searchBarRef = useRef<SearchBarHandle>(null)

  // #16 provider_detail_viewed — al abrirse el detalle de un prestador (card o pin).
  useEffect(() => {
    if (!selectedProfessional) return;
    trackProviderDetailViewed({
      prestadorId: selectedProfessional.CODIGO,
      especialidad: selectedProfessional.ESPECIALIDAD,
      tipoPrestador: selectedProfessional.RUBRO || '',
      tieneObservaciones: !!selectedProfessional.OBSERVACIONES?.trim(),
      posicionOriginal: (selectedProfessional.globalIndex ?? 0) + 1,
    });
  }, [selectedProfessional]);

  const handleSearchResults = (results: SearchResults) => {
    setSearchResults(results)
    setSelectedProfessional(null)
    setHasSearched(true)
  }

  const handleProfessionalSelect = (professional: Professional | null) => {
    setSelectedProfessional(professional)
  }

  const handleSpecialtyChange = (specialty: string) => {
    setSelectedSpecialty(specialty)
    setSearchResults(undefined)
    setSelectedProfessional(null)
    setHasSearched(false)
    setCartillaData(null)
    setSelectedLocation(null)
  }

  const handleCartillaChange = (cartilla: { nombrePdf: string; url: string } | null) => {
    setCartillaData(cartilla)
  }

  // "Todos": repite la búsqueda a nivel país (mismo plan/especialidad, sin
  // Pcia/Zona/localidad/barrio). La ubicación elegida NO se limpia — así los
  // resultados nacionales igual se ordenan por cercanía a ese punto.
  const handleShowAllCountry = () => {
    searchBarRef.current?.searchNationwide()
  }

  // Centro de cercanía. El GPS exacto ("Mi ubicación") tiene prioridad: si el
  // usuario compartió su posición, ordenamos por ella. Si no, usamos el centro
  // de la localidad elegida (tabla → centroide de esa localidad → centroide de
  // todos los resultados acotados [solo si NO es una búsqueda país] → centro
  // de la provincia, para pueblos sin prestadores geolocalizados y sin
  // resultados locales de dónde sacar un centroide).
  const center = useMemo<LatLng | null>(() => {
    if (gpsCenter) return gpsCenter

    if (selectedLocation) {
      const fromTable = localidadCenter(selectedLocation.SYSTEMLOCATION)
      if (fromTable) return fromTable

      const targetLoc = normalizeLocalityName(selectedLocation.SYSTEMLOCATION)
      const pts = (searchResults?.professionals ?? [])
        .filter((p) => normalizeLocalityName(p.LOCALIDAD) === targetLoc)
        .map((p) => parseCoords(p.LOCATION_LAT, p.LOCATION_LONG))
        .filter((c): c is LatLng => c !== null)
      const fromResults = centroidOf(pts)
      if (fromResults) return fromResults

      // Fallback: si se eligió una zona/provincia (sin localidad mapeada),
      // usamos el centroide de TODOS los resultados —que ya vienen acotados al
      // ámbito elegido— para que igual se ordene por cercanía y se muestren
      // distancias, en vez de quedar sin centro y caer al orden del backend.
      // OJO: solo vale si los resultados siguen acotados a la zona elegida —
      // en una búsqueda a nivel país (fallback o "Todos") "todos los
      // resultados" es todo el país, y promediarlos da un punto en el medio
      // del mapa sin relación con la localidad elegida (bug real: con Pérez,
      // Santa Fe, el mapa terminaba en Córdoba). Ahí se salta directo a la
      // provincia.
      if (!isNationwideSearch) {
        const allPts = (searchResults?.professionals ?? [])
          .map((p) => parseCoords(p.LOCATION_LAT, p.LOCATION_LONG))
          .filter((c): c is LatLng => c !== null)
        const fromAll = centroidOf(allPts)
        if (fromAll) return fromAll
      }

      // Último recurso: la localidad elegida es un pueblo sin prestadores
      // geolocalizados (no está en la tabla de centros, que se deriva de esos
      // prestadores) Y la búsqueda no encontró nada de dónde sacar un
      // centroide. Sin esto el mapa se queda sin centro y no se reposiciona.
      const fromProvincia = provinciaCenter(selectedLocation.PROVINCIA)
      if (fromProvincia) return fromProvincia
    }

    return null
  }, [selectedLocation, searchResults, gpsCenter, isNationwideSearch])

  // Reordena por cercanía al centro conservando TODOS los prestadores: primero los
  // geolocalizados por distancia real, y luego los sin coordenadas ordenados por la
  // distancia estimada al centro de su localidad (así los de la zona del usuario no
  // quedan enterrados al fondo, sino que encabezan ese grupo).
  const orderedResults = useMemo<SearchResults | undefined>(() => {
    if (!searchResults || !center) return searchResults
    const ranked = sortByDistanceHybrid(
      center,
      searchResults.professionals,
      (p) => ({ lat: p.LOCATION_LAT, lng: p.LOCATION_LONG }),
      (p) => p.LOCALIDAD,
    )
    return {
      ...searchResults,
      professionals: ranked.map((r) => ({
        ...r.item,
        distanceKm: r.distanceKm ?? undefined,
        distanceEstimatedKm: r.estimatedKm ?? undefined,
      })),
    }
  }, [searchResults, center])

  return (
    <div className={styles.medicalDirectory}>
      <SearchBar
        ref={searchBarRef}
        onSearchResults={handleSearchResults}
        onSpecialtyChange={handleSpecialtyChange}
        onCartillaChange={handleCartillaChange}
        onPlanChange={setSelectedPlan}
        onLocationSelect={setSelectedLocation}
        onUserCoords={setGpsCenter}
        onNationwideFallback={setNationwideFallbackActive}
        onNationwideSearch={setIsNationwideSearch}
      />
      {selectedSpecialty === "Salud mental" ? (
        <MentalHealth plan={selectedPlan} />
      ) : (
        <ResultsAndMapWrapper
          searchResults={orderedResults}
          selectedProfessional={selectedProfessional}
          onProfessionalSelect={handleProfessionalSelect}
          hasSearched={hasSearched}
          cartillaData={cartillaData}
          userCenter={center}
          onShowAllCountry={handleShowAllCountry}
          fallbackActive={nationwideFallbackActive}
          wideSearch={isNationwideSearch}
        />
      )}
    </div>
  )
}
